import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ClipboardList, FilterX } from "lucide-react";
import type { ColumnFilterMap } from "@pkhoffice/shared";
import { api } from "@/lib/api/client";
import { PageHeader } from "@/components/ui/PageHeader";
import { downloadFile } from "@/lib/api/download";
import { DataTable, type Column } from "@/components/ui/DataTable";
import { Pagination, DEFAULT_PAGE_SIZE } from "@/components/ui/Pagination";
import { SearchPanel, filterInputClass } from "@/components/ui/SearchPanel";
import {
  ColumnFilterCell,
  useColumnFilters,
  type ColumnFilterKind,
  type FacetOption,
} from "@/components/ui/ColumnFilter";

/**
 * Lưới báo cáo công việc dùng chung cho BC01 (Thực hiện CV) và BC11 (Kế hoạch CV) —
 * hệ cũ cũng dùng chung TreeTaskVM, chỉ khác tham số view=2 / view=3 và kỳ báo cáo
 * mặc định. Giữ nguyên 23 cột của lưới hệ cũ.
 */
export type WorkReportRow = {
  TASK_ID: number;
  SOURCE_NAME: string | null;
  TASK_TYPE2: number | null;
  TASK_NAME: string | null;
  COMPLETED_PERCENT: string | number | null;
  STATUS: number | null;
  CONTENT: string | null;
  PROJECT_NAME: string | null;
  START_TIME: string | null;
  END_TIME: string | null;
  TARGET: string | null;
  TASK_RESULT: string | null;
  DIFFICULT: string | null;
  PROPOSE: string | null;
  ASSIGN_ORG_NAME: string | null;
  COMMANDER_NAME: string | null;
  PERFORM_ORG_NAME: string | null;
  PERFORMER_NAME: string | null;
  ACTION_DATE: string | null;
  CREATED_DATE: string | null;
  CREATOR_NAME: string | null;
  UPDATED_DATE: string | null;
  UPDATER_NAME: string | null;
};

type Mode = "perform" | "plan" | "summary";
type Scope = "personal" | "dept";
type Filters = { q: string; status: string; fromDate: string; toDate: string };
const EMPTY: Filters = { q: "", status: "", fromDate: "", toDate: "" };

const STATUS_LABEL: Record<number, string> = {
  1: "Chưa thực hiện",
  2: "Đang thực hiện",
  3: "Hoàn thành",
  4: "Quá hạn",
  [-1]: "Đã huỷ",
};

const day = (v: string | null) => (v ? v.slice(0, 10) : "—");
const txt = (v: string | null) => v ?? "—";

/**
 * Kiểu ô lọc của từng cột, đúng như hàng `auxhead` bên hệ cũ: 6 ô chữ,
 * 1 khoảng số (Tiến độ), 4 khoảng ngày, còn lại là hộp chọn nhiều giá trị.
 */
const FILTER_KIND: Record<string, ColumnFilterKind> = {
  group: "list",
  class: "list",
  name: "text",
  progress: "number",
  status: "list",
  content: "text",
  project: "list",
  start: "date",
  end: "date",
  target: "text",
  result: "text",
  difficult: "text",
  propose: "text",
  assignOrg: "list",
  commander: "list",
  performOrg: "list",
  performer: "list",
  actionDate: "date",
  created: "date",
  creator: "list",
  updated: "date",
  updater: "list",
};

const cfParam = (cf: ColumnFilterMap) => (Object.keys(cf).length ? JSON.stringify(cf) : undefined);

async function fetchReport(
  mode: Mode,
  scope: Scope,
  page: number,
  pageSize: number,
  f: Filters,
  cf: ColumnFilterMap,
) {
  const { data } = await api.get("/task/report", {
    params: {
      mode,
      scope,
      page,
      pageSize: pageSize,
      q: f.q || undefined,
      status: f.status || undefined,
      fromDate: f.fromDate || undefined,
      toDate: f.toDate || undefined,
      cf: cfParam(cf),
    },
  });
  return (data.data ?? data) as {
    items: WorkReportRow[];
    total: number;
    period: { fromDate: string; toDate: string };
  };
}

export function WorkReportPage({
  mode,
  scope = "personal",
  title,
  breadcrumb,
}: {
  mode: Mode;
  /** "dept" là màn "Công việc Phòng ban" (tree_task_unit.zul?view=1) của hệ cũ. */
  scope?: Scope;
  title: string;
  breadcrumb: string;
}) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [filters, setFilters] = useState<Filters>(EMPTY);
  const [applied, setApplied] = useState<Filters>(EMPTY);
  const cols = useColumnFilters(() => setPage(1));
  /** Cột đang mở hộp chọn — chỉ hỏi giá trị khi người dùng thật sự mở nó. */
  const [facetColumn, setFacetColumn] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["work-report", mode, scope, page, pageSize, applied, cols.filters],
    queryFn: () => fetchReport(mode, scope, page, pageSize, applied, cols.filters),
  });

  const { data: facets, isFetching: facetsLoading } = useQuery({
    queryKey: ["work-report-facet", mode, scope, applied, cols.filters, facetColumn],
    enabled: facetColumn !== null,
    queryFn: async () => {
      const { data } = await api.get("/task/report/facet", {
        params: {
          mode,
          scope,
          column: facetColumn,
          q: applied.q || undefined,
          status: applied.status || undefined,
          fromDate: applied.fromDate || undefined,
          toDate: applied.toDate || undefined,
          cf: cfParam(cols.filters),
        },
      });
      const opts = (data.data ?? data) as FacetOption[];
      // Cột Trạng thái hiển thị bằng chữ trong lưới, nên hộp chọn cũng phải là
      // chữ — dùng chung một bảng nhãn để hai chỗ không trôi khỏi nhau.
      if (facetColumn === "status")
        return opts.map((o) => ({
          ...o,
          label: o.value == null ? "(trống)" : (STATUS_LABEL[Number(o.value)] ?? `#${o.value}`),
        }));
      return opts;
    },
  });

  /** Gắn ô lọc vào từng cột theo `FILTER_KIND`; cột STT không lọc. */
  const withFilters = (list: Column<WorkReportRow>[]): Column<WorkReportRow>[] =>
    list.map((c) => {
      const kind = FILTER_KIND[c.key];
      if (!kind) return c;
      return {
        ...c,
        filter: (
          <ColumnFilterCell
            kind={kind}
            value={cols.filters[c.key]}
            onChange={(v) => cols.set(c.key, v)}
            facets={facetColumn === c.key ? facets : undefined}
            facetsLoading={facetColumn === c.key && facetsLoading}
            onOpenFacets={() => setFacetColumn(c.key)}
          />
        ),
      };
    });

  const columns: Column<WorkReportRow>[] = [
    {
      key: "stt",
      header: "STT",
      width: "56px",
      align: "center",
      render: (_r, i) => (
        <span className="text-muted-foreground tabular-nums">{(page - 1) * pageSize + i + 1}</span>
      ),
    },
    { key: "group", header: "Nhóm công việc", width: "150px", render: (r) => txt(r.SOURCE_NAME) },
    {
      key: "class",
      header: "Phân loại",
      width: "110px",
      render: (r) => (r.TASK_TYPE2 == null ? "—" : String(r.TASK_TYPE2)),
    },
    {
      key: "name",
      header: "Tên công việc",
      width: "240px",
      render: (r) => <span className="font-medium">{txt(r.TASK_NAME)}</span>,
    },
    {
      key: "progress",
      header: "Tiến độ",
      width: "90px",
      align: "right",
      render: (r) => (
        <span className="tabular-nums">
          {r.COMPLETED_PERCENT == null ? "—" : `${Math.round(Number(r.COMPLETED_PERCENT))}%`}
        </span>
      ),
    },
    {
      key: "status",
      header: "Trạng thái",
      width: "130px",
      render: (r) => (r.STATUS == null ? "—" : (STATUS_LABEL[r.STATUS] ?? `#${r.STATUS}`)),
    },
    { key: "content", header: "Nội dung công việc", width: "260px", render: (r) => txt(r.CONTENT) },
    { key: "project", header: "Dự án", width: "130px", render: (r) => txt(r.PROJECT_NAME) },
    {
      key: "start",
      header: "Ngày phát sinh",
      width: "120px",
      render: (r) => <span className="tabular-nums text-sm">{day(r.START_TIME)}</span>,
    },
    {
      key: "end",
      header: "Hạn hoàn thành",
      width: "120px",
      render: (r) => <span className="tabular-nums text-sm">{day(r.END_TIME)}</span>,
    },
    {
      key: "target",
      header: "Mục tiêu, kết quả mong muốn",
      width: "220px",
      render: (r) => txt(r.TARGET),
    },
    {
      key: "result",
      header: "Kết quả thực hiện",
      width: "200px",
      render: (r) => txt(r.TASK_RESULT),
    },
    { key: "difficult", header: "Nguyên nhân", width: "180px", render: (r) => txt(r.DIFFICULT) },
    { key: "propose", header: "Đề xuất", width: "180px", render: (r) => txt(r.PROPOSE) },
    {
      key: "assignOrg",
      header: "Bộ phận giao việc",
      width: "180px",
      render: (r) => txt(r.ASSIGN_ORG_NAME),
    },
    {
      key: "commander",
      header: "Người giao việc",
      width: "160px",
      render: (r) => txt(r.COMMANDER_NAME),
    },
    {
      key: "performOrg",
      header: "Bộ phận thực hiện",
      width: "180px",
      render: (r) => txt(r.PERFORM_ORG_NAME),
    },
    {
      key: "performer",
      header: "Người thực hiện",
      width: "160px",
      render: (r) => txt(r.PERFORMER_NAME),
    },
    {
      key: "actionDate",
      header: "Ngày thực hiện",
      width: "120px",
      render: (r) => <span className="tabular-nums text-sm">{day(r.ACTION_DATE)}</span>,
    },
    {
      key: "created",
      header: "Ngày tạo",
      width: "110px",
      render: (r) => <span className="tabular-nums text-sm">{day(r.CREATED_DATE)}</span>,
    },
    { key: "creator", header: "Người tạo", width: "150px", render: (r) => txt(r.CREATOR_NAME) },
    {
      key: "updated",
      header: "Ngày cập nhật",
      width: "120px",
      render: (r) => <span className="tabular-nums text-sm">{day(r.UPDATED_DATE)}</span>,
    },
    {
      key: "updater",
      header: "Người cập nhật",
      width: "150px",
      render: (r) => txt(r.UPDATER_NAME),
    },
  ];

  const period = data?.period;

  /** Nút "Xuất báo cáo" — hệ cũ có ở mọi lưới báo cáo. */
  const onExport = () =>
    downloadFile(
      "/task/report/export",
      {
        mode,
        q: applied.q,
        status: applied.status,
        fromDate: applied.fromDate,
        toDate: applied.toDate,
        cf: cfParam(cols.filters) ?? "",
      },
      `${mode === "plan" ? "bc11" : "bc01"}.csv`,
    );

  return (
    <div>
      <PageHeader
        title={title}
        subtitle={
          period
            ? `${(data?.total ?? 0).toLocaleString("vi-VN")} công việc · kỳ ${period.fromDate} → ${period.toDate}`
            : `${(data?.total ?? 0).toLocaleString("vi-VN")} công việc`
        }
        breadcrumb={breadcrumb}
      />
      <SearchPanel
        fields={[
          {
            label: "Tìm kiếm",
            control: (
              <input
                className={filterInputClass}
                placeholder="Tên công việc, nội dung, nguyên nhân…"
                value={filters.q}
                onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
              />
            ),
          },
          {
            label: "Trạng thái tìm kiếm",
            control: (
              <select
                className={filterInputClass}
                value={filters.status}
                onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
              >
                <option value="">---- Tất cả ----</option>
                {Object.entries(STATUS_LABEL).map(([v, label]) => (
                  <option key={v} value={v}>
                    {label}
                  </option>
                ))}
              </select>
            ),
          },
          {
            label: "Từ ngày",
            control: (
              <input
                type="date"
                className={filterInputClass}
                value={filters.fromDate || (period?.fromDate ?? "")}
                onChange={(e) => setFilters((f) => ({ ...f, fromDate: e.target.value }))}
              />
            ),
          },
          {
            label: "Đến ngày",
            control: (
              <input
                type="date"
                className={filterInputClass}
                value={filters.toDate || (period?.toDate ?? "")}
                onChange={(e) => setFilters((f) => ({ ...f, toDate: e.target.value }))}
              />
            ),
          },
        ]}
        onSubmit={() => {
          setApplied(filters);
          setPage(1);
        }}
        onExport={onExport}
      />
      <div className="overflow-x-auto">
        <div className="min-w-[3200px]">
          {cols.count > 0 && (
            <div className="mb-1 flex justify-end">
              <button
                type="button"
                onClick={cols.clearAll}
                className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-xs text-muted-foreground hover:bg-muted"
              >
                <FilterX className="h-3.5 w-3.5" /> Bỏ tất cả bộ lọc ({cols.count})
              </button>
            </div>
          )}
          <DataTable<WorkReportRow>
            columns={withFilters(columns)}
            rows={data?.items ?? []}
            keyBy={(r, i) => `${r.TASK_ID}-${i}`}
            isLoading={isLoading}
            empty={
              <>
                <ClipboardList className="inline h-4 w-4 mr-1" /> Không có dữ liệu!
              </>
            }
            dense
          />
          <Pagination
            page={page}
            pageSize={pageSize}
            total={data?.total ?? 0}
            onPageChange={setPage}
            onPageSizeChange={(n) => {
              setPageSize(n);
              setPage(1);
            }}
          />
        </div>
      </div>
    </div>
  );
}
